import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { GetLeaves, GetLeaveBalances, CreateLeave, UpdateLeaveStatus, DeleteLeave } from "../Api";
import { ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";
import { DEFAULT_STALE_TIME } from "../Constant/AppConstants";

export const leaveQueryKeys = {
  leaves: (params?: Parameters<typeof GetLeaves>[0]) =>
    ["leaves", params] as const,
  balances: (params?: Parameters<typeof GetLeaveBalances>[0]) =>
    ["leave-balances", params] as const,
};

export const useLeavesQuery = (
  params?: Parameters<typeof GetLeaves>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: leaveQueryKeys.leaves(params),
    queryFn: async () => unwrapApiResponse(await GetLeaves(params)),
  });
};

export const useLeaveBalancesQuery = (
  params: Parameters<typeof GetLeaveBalances>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    enabled: !!params?.employee_id,
    ...options,
    queryKey: leaveQueryKeys.balances(params),
    queryFn: async () => unwrapApiResponse(await GetLeaveBalances(params)),
  });
};

export const useCreateLeaveMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateLeave>[0]) =>
      CreateLeave(payload),
    onSuccess: (res) => {
      if (res?.success) {
        queryClient.invalidateQueries({ queryKey: ["leaves"] });
        queryClient.invalidateQueries({ queryKey: ["leave-balances"] });
      }
    },
  });
};

export const useUpdateLeaveStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateLeaveStatus>[0]) =>
      UpdateLeaveStatus(payload),
    onSuccess: (res) => {
      if (res?.success) {
        queryClient.invalidateQueries({ queryKey: ["leaves"] });
        queryClient.invalidateQueries({ queryKey: ["leave-balances"] });
      }
    },
  });
};

export const useDeleteLeaveMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => DeleteLeave(id),
    onSuccess: (res) => {
      if (res?.success) {
        queryClient.invalidateQueries({ queryKey: ["leaves"] });
        queryClient.invalidateQueries({ queryKey: ["leave-balances"] });
      }
    },
  });
};
